import { Component, PureComponent, MouseEvent, CSSProperties, MouseEventHandler } from 'react'
import * as cn from 'classnames'
import Scrollable from 'components/atoms/Scrollable'
import * as styles from 'styles/atoms/dropdowns/InlineDropdownWrapper.css'
import { QAMarked } from 'interfaces/component'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import withContext from 'hocs/withContext/withContext'

export type InlineDropdownCheckSelected<TEntity> = (e: MouseEvent<HTMLElement>) => TEntity | null

export type InlineDropdownWrapperProps<TEntity> = QAMarked & {
  getCaption: (entity: TEntity) => string
  withArrow?: boolean
  selected: TEntity
  className?: string
  captionClassName?: string

  checkSelected?: InlineDropdownCheckSelected<TEntity>
  onSelect?: (selectedOption: TEntity) => void
}

type InlineDropdownWrapperInnerProps<TEntity> =
  & InlineDropdownWrapperProps<TEntity>
  & { outerEventTracker: OuterEventTrackerContextType }

type InlineDropdownWrapperState = {
  isExpanded: boolean
  dropdownStyles: CSSProperties
}

class InlineDropdownWrapperInner<TEntity extends { id: any }> extends Component<InlineDropdownWrapperInnerProps<TEntity>, InlineDropdownWrapperState> {
  static defaultProps = {
    withArrow: true,
  }

  state = {
    isExpanded: false,
    dropdownStyles: {},
  }

  private dropdownNode: HTMLDivElement | null = null
  private eventTrackersRegistered = false

  render () {
    const { isExpanded, dropdownStyles } = this.state
    const {
      selected,
      withArrow,
      getCaption,
      qaMarker,
      children,
      className,
      captionClassName,
    } = this.props

    return (
      <div
        className={ cn(styles.inlineDropdown, { [styles.expanded]: isExpanded }, className) }
        data-qa-marker={ qaMarker }
      >
        <a
          className={ cn(styles.caption, captionClassName) }
          onClick={ this.handleCaptionClick }
        >
          { getCaption(selected) }
          { withArrow && <i className={ styles.arrow } /> }
        </a>
        { isExpanded && (
          <div
            ref={ (el) => this.dropdownNode = el }
            className={ styles.dropdownContainer }
            style={ dropdownStyles }
          >
            <Scrollable contentClassName={ styles.dropdownScrollable }>
              <div className={ styles.dropdownContent } onClick={ this.handleContentClick }>
                { children }
              </div>
            </Scrollable>
          </div>
        )}
      </div>
    )
  }

  componentDidMount () {
    this.placeDropdown()
    this.manageEventTrackers()
  }

  componentDidUpdate () {
    /*
      TODO: caused by FRO-1294
      A dropdown may appear out page bounds. If so a page scroll appears.
      Then we adjust dropdown's position placing it inside the bounds and scroll disappears.
      But this effect triggers a `scroll` event which we are subscribed for and which we close a popup upon.

      Therefore we need to unsubscribe of this event first.
      But we can't subscribe just afterwards because page reflow has not been performed yet.
      `setTimeout` is here to solve this issue.
     */
    this.unregisterEventTrackers()
    this.placeDropdown()
    setTimeout(this.manageEventTrackers, 0)
  }

  componentWillUnmount () {
    this.unregisterEventTrackers()
  }

  private placeDropdown = () => {
    if (!this.state.isExpanded || !this.dropdownNode) {
      return
    }

    const dropdownRect = this.dropdownNode.getBoundingClientRect()

    let offsetX = 0
    // a multiplier using for calculation a sign of window edge offset
    let deltaMultiplier = 0
    if (dropdownRect.left < 0) {
      offsetX = -dropdownRect.left
      deltaMultiplier = 1
    } else if (dropdownRect.right > window.innerWidth) {
      offsetX = window.innerWidth - dropdownRect.right
      deltaMultiplier = -1
    }
    const translateX = `calc(${ offsetX }px + (${ deltaMultiplier } * 1rem))`

    let offsetY = 0
    deltaMultiplier = 0
    if (dropdownRect.top < 0) {
      offsetY = -dropdownRect.top
      deltaMultiplier = 1
    } else if (dropdownRect.bottom > window.innerHeight) {
      offsetY = window.innerHeight - dropdownRect.bottom
      deltaMultiplier = -1
    }
    const translateY = `calc(${ offsetY }px + (${ deltaMultiplier } * 1rem))`

    this.dropdownNode.style.transform = `translate(${ translateX }, ${ translateY })`
  }

  private toggleDropdown = (toggleValue: boolean) => {
    if (this.state.isExpanded === toggleValue) {
      return
    }

    this.setState({ isExpanded: toggleValue })
  }

  private closeDropdown = () => {
    this.toggleDropdown(false)
  }

  private manageEventTrackers = () => {
    if (this.state.isExpanded) {
      this.registerEventTrackers()
    } else {
      this.unregisterEventTrackers()
    }
  }

  private registerEventTrackers = () => {
    if (this.eventTrackersRegistered) {
      return
    }

    this.eventTrackersRegistered = true
    // TODO attach this listener on bubbling stage to avoid a scenario:
    // 1. popup is opened
    // 2. click to a caption (outer click comparing to a dropdown)
    // outer click listener is being triggered, it closes the popup.
    // Then the click is being captured to the actual `caption`, where `handleCaptionClick` is being fired opening popup.
    const { outerEventTracker } = this.props
    outerEventTracker.addOuterEventListener('tap click', this.closeDropdown, this.dropdownNode)
    outerEventTracker.addOuterEventListener('scroll', this.closeDropdown, document.body)
  }

  private unregisterEventTrackers = () => {
    if (!this.eventTrackersRegistered) {
      return
    }

    const { outerEventTracker } = this.props
    this.eventTrackersRegistered = false
    outerEventTracker.removeOuterEventListener('tap click', this.closeDropdown)
    outerEventTracker.removeOuterEventListener('scroll', this.closeDropdown)
  }

  private handleCaptionClick = () => {
    this.setState({
      isExpanded: !this.state.isExpanded,
    })
  }

  private handleContentClick: MouseEventHandler<HTMLDivElement> = (e) => {
    const { checkSelected, onSelect } = this.props
    if (!checkSelected) {
      this.closeDropdown()
      return
    }

    const selected = checkSelected(e)

    if (!selected) {
      return
    }

    if (selected.id !== this.props.selected.id && onSelect) {
      onSelect(selected)
    }

    this.closeDropdown()
  }
}

const InlineDropdownWrapperWithContext = withContext(OuterEventTrackerContext, 'outerEventTracker')(
  InlineDropdownWrapperInner
)

// It's a trick to get working together typings with generic and decorator withContext
class InlineDropdownWrapper<TEntity extends { id: any }> extends PureComponent<InlineDropdownWrapperProps<TEntity>> {
  render () {
    return (
      <InlineDropdownWrapperWithContext { ...this.props } />
    )
  }
}

export default InlineDropdownWrapper
